/**
 * Resolver pure logic (Phase Wellspring W2b).
 *
 * The dependency-FREE routing / mapping / shaping logic the resolve-matches Edge
 * Function uses; the EF shell owns all I/O (Keepa fetch, Supabase writes, pacing).
 * Kept a TRUE LEAF (zero imports — not even matchScore/upcNormalize) so the Deno
 * EF can bundle it through the supabase/functions/_shared re-export shim with no
 * nested-import resolution (the app imports extensionless; Deno needs explicit
 * .ts — a leaf sidesteps that conflict, the matchScore.ts/upcNormalize.ts precedent).
 *
 * The EF calls upcNormalize + scoreMatch directly (leaf libs, via their own shims)
 * and feeds the results here. The attribute shapes returned are STRUCTURALLY a
 * matchScore MatchAttributes (so the EF can pass them straight to scoreMatch) —
 * intentionally not imported, to keep this a leaf.
 */

/** The products columns the resolver reads (the drain SELECT). */
export interface ResolverProductRow {
  id: string;
  // isg_sku removed (U3 F3): products.isg_sku is retired to variants.isg_sku; the resolver never read
  // it functionally — the EF drain no longer selects it, and error labels key on products.id.
  supplier_part_no: string | null;
  mpn: string | null;
  model: string | null;
  title: string | null;
  brand: string | null;
  upc: string | null;
}

/** Structurally a matchScore MatchAttributes (one side of a comparison). */
export interface ResolverAttributes {
  supplierPartNo: string | null;
  mpn: string | null;
  model: string | null;
  title: string | null;
  brand: string | null;
}

/** A parsed Keepa candidate (the EF parses the Keepa product JSON into this). */
export interface KeepaCandidate {
  asin: string | null;
  title: string | null;
  brand: string | null;
  manufacturer: string | null;
  partNumber: string | null;
  model: string | null;
}

/** A Keepa candidate after scoring (the EF runs scoreMatch and fills these). */
export interface ScoredCandidate {
  candidate: KeepaCandidate;
  score: number;
  verdict: string; // 'trust' | 'suspect' | 'hold'
  breakdown: unknown; // the matchScore per-attribute breakdown (stored as jsonb)
}

/** A product_candidates INSERT row (status defaults to 'candidate' in the DB). */
export interface ProductCandidateInsert {
  supplier_product_id: string;
  candidate_asin: string;
  title: string | null;
  brand: string | null;
  part_no: string | null;
  arbi_confidence_score: number;
  breakdown: unknown;
  verdict: string;
  rank: number;
  source: string;
}

export type ResolverArm = 'clean-upc' | 'fuzzy';
export type ResolveOutcome = 'deterministic-asin' | 'candidates' | 'no-listing';

function s(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  return t.length ? t : null;
}

/** Map a products row → the supplier side of a match comparison. */
export function rowToSupplierAttributes(row: ResolverProductRow): ResolverAttributes {
  return {
    supplierPartNo: s(row.supplier_part_no),
    mpn: s(row.mpn),
    model: s(row.model),
    title: s(row.title),
    brand: s(row.brand),
  };
}

/** Map a Keepa candidate → the candidate side of a match comparison (part# union: mpn←partNumber, model←model). */
export function keepaCandidateToAttributes(c: KeepaCandidate): ResolverAttributes {
  return {
    supplierPartNo: null,
    mpn: s(c.partNumber),
    model: s(c.model),
    title: s(c.title),
    brand: s(c.brand) ?? s(c.manufacturer),
  };
}

/**
 * Normalize a part# for keyword search (W3b recall): strip the Excel numeric `.0` coercion
 * artifact (a UPC/part# read from a sheet as a number arrives as "536342.0") and collapse internal
 * whitespace. A no-op on already-clean codes (so the validated clean-row behavior is unchanged).
 */
export function normalizePartNo(v: string | null): string | null {
  if (v === null) return null;
  const t = v.replace(/\.0+$/, '').replace(/\s+/g, ' ').trim();
  return t.length ? t : null;
}

// Coarse title→search-term cleanup for the recall fallback: keep alphanumerics, collapse the rest
// to spaces, cap to the first 8 tokens (a focused descriptive query). null when nothing usable.
function cleanTitleForSearch(title: string | null): string | null {
  if (!title) return null;
  const cleaned = title
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 8)
    .join(' ');
  return cleaned.length ? cleaned : null;
}

/** The keyword-search term for the fuzzy arm: brand + the first available (normalized) part#. */
export function buildSearchTerm(row: ResolverProductRow): string {
  const partNo = normalizePartNo(s(row.supplier_part_no) ?? s(row.mpn) ?? s(row.model));
  return [s(row.brand), partNo]
    .filter((x): x is string => x !== null)
    .join(' ')
    .trim();
}

/**
 * The fallback search term for the W3b bounded recall retry — a DIFFERENT angle than the primary
 * brand+part#, tried once when the primary returns 0 candidates. Strategy: (1) title-derived
 * (brand + the cleaned descriptive title) — the high-value angle for the cryptic-supplier-code tail
 * (W2a Section B: the supplier part# misses Keepa, e.g. `NETWAY5PQX`, but the descriptive title
 * carries the real identifier, "NETWAY5PX WITH LINQ TECH"); (2) else the bare normalized part#
 * (drop the brand — a wrong/misindexed brand can be the recall killer). Returns '' when there is no
 * distinct second angle, so the caller skips the retry (a genuine miss stays terminal no-listing).
 */
export function buildFallbackSearchTerm(row: ResolverProductRow): string {
  const primary = buildSearchTerm(row);
  const title = cleanTitleForSearch(s(row.title));
  if (title) {
    const t = [s(row.brand), title]
      .filter((x): x is string => x !== null)
      .join(' ')
      .trim();
    if (t && t !== primary) return t;
  }
  const partNo = normalizePartNo(s(row.supplier_part_no) ?? s(row.mpn) ?? s(row.model));
  if (partNo && partNo !== primary) return partNo;
  return '';
}

/**
 * The clean-vs-fuzzy routing decision. `cleanUpc` is the EF's normalizeUpc result
 * (the valid 12-digit value, or null when the UPC is absent/invalid).
 */
export function routeForRow(cleanUpc: string | null): ResolverArm {
  return cleanUpc ? 'clean-upc' : 'fuzzy';
}

/**
 * Classify a row's resolution outcome from the EF's I/O results:
 *  - clean-upc + a code-lookup ASIN  → deterministic-asin (write products.asin)
 *  - clean-upc + no code hit         → no-listing (UPC not on Amazon)
 *  - fuzzy + ≥1 scored candidate     → candidates (write product_candidates)
 *  - fuzzy + 0 candidates            → no-listing (Keepa returned nothing usable)
 */
export function classifyOutcome(args: {
  arm: ResolverArm;
  codeHitAsin: string | null;
  scoredCount: number;
}): ResolveOutcome {
  if (args.arm === 'clean-upc') return args.codeHitAsin ? 'deterministic-asin' : 'no-listing';
  return args.scoredCount > 0 ? 'candidates' : 'no-listing';
}

/**
 * Shape scored Keepa candidates into product_candidates INSERT rows: drop
 * asin-less candidates, dedupe by candidate_asin (keep the highest score — the
 * UNIQUE(supplier_product_id, candidate_asin) constraint), rank 1-based by score
 * descending, cap to the top CANDIDATE_SOURCE_CAP, tag the source.
 */
// W4c — write-side cap on stored candidates per supplier row (top-N by score). The resolver
// scores ~20/row; 10 = 2× the W4a render cap of 5, leaving show-more headroom. Pure; pre-W4c
// rows already stored are untouched (and wiped at the Day-103 reset regardless).
export const CANDIDATE_SOURCE_CAP = 10;
export function shapeCandidateRows(
  supplierProductId: string,
  scored: ScoredCandidate[],
  source: string
): ProductCandidateInsert[] {
  const byAsin = new Map<string, ScoredCandidate>();
  for (const sc of scored) {
    const asin = s(sc.candidate.asin);
    if (!asin) continue;
    const prev = byAsin.get(asin);
    if (!prev || sc.score > prev.score) byAsin.set(asin, sc);
  }
  return [...byAsin.values()]
    .sort((a, b) => b.score - a.score)
    .slice(0, CANDIDATE_SOURCE_CAP)
    .map((sc, i) => ({
      supplier_product_id: supplierProductId,
      candidate_asin: s(sc.candidate.asin) as string,
      title: s(sc.candidate.title),
      brand: s(sc.candidate.brand) ?? s(sc.candidate.manufacturer),
      part_no: s(sc.candidate.partNumber) ?? s(sc.candidate.model),
      arbi_confidence_score: sc.score,
      breakdown: sc.breakdown,
      verdict: sc.verdict,
      rank: i + 1,
      source,
    }));
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// W6.2 — Catalog resolver swap (HYBRID). The pure three-tier empty-trigger routing + the recall-
// cohort predicate. (The EF shell injects the actual Catalog/Keepa I/O runners.)
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** The resolver source tags the HYBRID arm emits (one per resolving tier). */
export const SOURCE_CATALOG = 'spapi_catalog'; // Tier 1 — Catalog primary
export const SOURCE_CATALOG_FALLBACK = 'spapi_catalog_fallback'; // Tier 2 — Catalog fallback-term
export const SOURCE_KEEPA_FALLBACK = 'keepa_keyword_fallback'; // Tier 3 — Keepa empty-trigger (+ legacy)

/**
 * W6.2 — recall-cohort predicate. A candidate's `source` marks the lower-precision recall cohort
 * (sorted below the primary matches + badged "broad match" in ProductDetail) iff it ends in
 * `_fallback` — i.e. `spapi_catalog_fallback` (Tier 2) or `keepa_keyword_fallback` (Tier 3 + any
 * legacy rows). The Tier-1 primary `spapi_catalog` (and legacy `keepa_keyword`) are NOT fallbacks.
 * Generalizes the former literal `source === 'keepa_keyword_fallback'` so the swap's provider-
 * accurate tags register without a per-tag render change.
 */
export function isRecallFallback(source: string | null | undefined): boolean {
  return !!source && /_fallback$/.test(source);
}

/** One tier's resolution attempt: `scored` candidates (possibly empty) + `ok` (false = a transient
 * provider failure — 429/timeout — so the caller leaves the row retry-later, no marker). */
export interface TierResult {
  scored: ScoredCandidate[];
  ok: boolean;
}

/** The outcome of the HYBRID three-tier fuzzy routing: the resolving tier's `scored` + `source`,
 * the `tier` that supplied them (null = none / no-listing), and `transient` (a provider failure
 * ⇒ retry-later, leave match_resolved_at NULL). */
export interface FuzzyResolution {
  scored: ScoredCandidate[];
  source: string;
  tier: 1 | 2 | 3 | null;
  transient: boolean;
}

/**
 * W6.2 HYBRID three-tier empty-trigger routing (PURE — the EF injects the actual Catalog/Keepa
 * runners). STRICT empty-trigger ordering; exactly ONE tier supplies a row's candidates:
 *   Tier 1  Catalog primary (paginated)                                       → spapi_catalog
 *   Tier 2  Catalog fallback-term (only if Tier 1 empty AND a DISTINCT angle)  → spapi_catalog_fallback
 *   Tier 3  Keepa /search on the PRIMARY term (only if BOTH Catalog tiers empty) → keepa_keyword_fallback
 *   else    no-listing (scored empty, tier null)
 * A tier's `ok:false` (transient) short-circuits to {transient:true} — the row stays retry-later and
 * the lower tiers are NOT consulted (a transient miss is not an "empty" trigger). Because each tier
 * fires only on the prior's EMPTY, the Keepa call never runs when Catalog returns candidates — the
 * "armed but cheap" guarantee.
 */
export async function routeFuzzyResolution(args: {
  primaryTerm: string;
  fallbackTerm: string;
  catalog: (keywords: string) => Promise<TierResult>;
  keepa: (term: string) => Promise<TierResult>;
}): Promise<FuzzyResolution> {
  // TIER 1 — Catalog primary
  const t1 = await args.catalog(args.primaryTerm);
  if (!t1.ok) return { scored: [], source: SOURCE_CATALOG, tier: null, transient: true };
  if (t1.scored.length > 0)
    return { scored: t1.scored, source: SOURCE_CATALOG, tier: 1, transient: false };

  // TIER 2 — Catalog fallback-term (only a DISTINCT second angle; else fall straight through)
  if (args.fallbackTerm && args.fallbackTerm !== args.primaryTerm) {
    const t2 = await args.catalog(args.fallbackTerm);
    if (!t2.ok) return { scored: [], source: SOURCE_CATALOG_FALLBACK, tier: null, transient: true };
    if (t2.scored.length > 0)
      return { scored: t2.scored, source: SOURCE_CATALOG_FALLBACK, tier: 2, transient: false };
  }

  // TIER 3 — Keepa empty-trigger insurance (ONE /search on the PRIMARY term)
  const t3 = await args.keepa(args.primaryTerm);
  if (!t3.ok) return { scored: [], source: SOURCE_KEEPA_FALLBACK, tier: null, transient: true };
  if (t3.scored.length > 0)
    return { scored: t3.scored, source: SOURCE_KEEPA_FALLBACK, tier: 3, transient: false };

  // all three empty ⇒ no-listing (source moot — no candidates written)
  return { scored: [], source: SOURCE_CATALOG, tier: null, transient: false };
}
